import React, {useEffect} from 'react';
import {getWindow} from 'ssr-window';
import {useSelector} from 'react-redux';
import {Helmet} from 'react-helmet-async';
import {useRouteMatch} from 'react-router-dom';

import {Box} from '@spothero/ui';

import {formatSlices} from 'utils/cms';
import {getCanonicalUrl, Page} from 'utils/page-utils';
import trackMarketingLanding from 'segment/pages/marketing-landing';
import FAQPageStructuredData from 'cms/seo/faq-page-structured-data';
import WithSsrRedirectTrailingSlash from 'router/WithSsrRedirectTrailingSlash';

const window = getWindow();

const DefaultMarketingPage = () => {
    // Returned Values from Prismic
    const {
        body: slices,
        meta_title_marketing: metaTitleMarketing,
        meta_description_marketing: metaDescriptionMarketing,
        opengraph_title: openGraphTitle,
        opengraph_image: openGraphImage,
        opengraph_description: openGraphDescription,
        pageType,
    } = useSelector(state => state.page);
    // For canonical and og:url urls remove any trailing slash
    const canonicalUrl = getCanonicalUrl(window.location.href);
    // Open Graph Conditionals
    const ogTitle = openGraphTitle || metaTitleMarketing;
    const ogDescription = openGraphDescription || metaDescriptionMarketing;

    /* --------------------------------------------------------------- *\
        Renders the proper meta tag depending on if the page falls
        under the `/prismic-preview/` path or not. On non preview
        pages, the robots meta tag is swapped out for the canonical
        link tag. (noindex, nofollow pages should not have a
        canonical link tag present)
    \* --------------------------------------------------------------- */
    // eslint-disable-next-line padding-line-between-statements
    const match = useRouteMatch();
    const isPreviewPage = /prismic-preview/i.test(match.path);
    const conditionalMetaTag = isPreviewPage ? (
        <meta name="robots" content="none" />
    ) : (
        <link rel="canonical" href={canonicalUrl} />
    );

    useEffect(() => {
        trackMarketingLanding({
            pageType: Page.MARKETING,
            page: canonicalUrl,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="MarketingPage">
            <Helmet>
                <title>{metaTitleMarketing}</title>
                {conditionalMetaTag}
                {/* ---------------------------------------------------------- *\
                        In order for the user-entered meta description to
                        override the default meta description created within
                        the ApplicationMeta file, this description meta tag must
                        have the same attributes as the default (name, itemProp,
                        and content).
                \* ---------------------------------------------------------- */}
                <meta
                    name="description"
                    itemProp="description"
                    content={metaDescriptionMarketing}
                />
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:url" content={canonicalUrl} />
                {openGraphImage ? (
                    <meta property="og:image" content={openGraphImage} />
                ) : null}
                <meta property="og:type" itemProp="og:type" content="website" />

                <body className="standalone-page marketing-page" />
            </Helmet>

            <FAQPageStructuredData slices={slices} />

            <Box as="main">{formatSlices({slices, pageType})}</Box>
        </div>
    );
};

export default WithSsrRedirectTrailingSlash(DefaultMarketingPage);
